import { cn } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import { AlertTriangle, ArrowDown, RefreshCw, Repeat2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  CAPACITY_FORECAST,
  SWAP_SUGGESTIONS,
  type BookingException,
  type BookingProposal,
  type CapacityForecastRow,
  type SwapSuggestion,
} from '../lib/mockData';
import { SailingTypePill } from './SailingTypePill';

const TABS = ['All', 'Urgent', 'Backlog', 'Swaps'] as const;
type Tab = (typeof TABS)[number];

/** "Thinking" steps shown in sequence on open before suggestions settle. */
const PROCESSING_STEPS = ['Scanning pipeline', 'Solving constraints', 'Proposing'] as const;
const PROCESSING_STEP_MS = 800;

/**
 * "my i" — Booking Intelligence. Same sliding right-hand shell as the Plan
 * screen's Movement Intelligence: slate-800 header (icon closes), chip tabs,
 * scrollable body. Presentation only — the Run / Storm actions, proposal
 * confirmation and capacity bars are mock; the constraint engine + operator
 * APIs that would feed them are parked.
 */
export function BookingIntelligence({
  open,
  onClose,
  depotLabel,
  horizonLabel,
  pipelineCount,
  exceptions,
  proposals,
  onRerun,
  rerunning,
}: {
  open: boolean;
  onClose: () => void;
  depotLabel: string;
  horizonLabel: string;
  /** Orders in the To Book queue for the current depot/horizon. */
  pipelineCount: number;
  /** Real exceptions derived from the To-Book rows (no route / cutoff passed). */
  exceptions: BookingException[];
  /** Real proposals derived from the To-Book rows (catchable recommendation). */
  proposals: BookingProposal[];
  /** Re-pull the worklist + re-stamp "now" so routes/cutoffs/counts recompute. */
  onRerun: () => void;
  /** Worklist is refetching — disable the rerun control + spin its icon. */
  rerunning: boolean;
}) {
  const [tab, setTab] = useState<Tab>('All');
  // Which proposals the dispatcher has ticked to include (by order id) — kept
  // local; the proposals themselves are real, recomputed upstream on Rerun.
  const [confirmedIds, setConfirmedIds] = useState<Set<string>>(new Set());
  const [swaps, setSwaps] = useState<SwapSuggestion[]>(SWAP_SUGGESTIONS);
  const [appliedSwaps, setAppliedSwaps] = useState<Set<string>>(new Set());

  // Scan when the panel opens — and again on each Rerun (scanKey bump): walk the
  // "thinking" steps (≥1 each), then settle.
  const [scanning, setScanning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [scanKey, setScanKey] = useState(0);
  useEffect(() => {
    if (!open) {
      setScanning(false);
      return;
    }
    setScanning(true);
    setStepIndex(0);
    const timers = PROCESSING_STEPS.slice(1).map((_, i) =>
      setTimeout(() => setStepIndex(i + 1), (i + 1) * PROCESSING_STEP_MS),
    );
    timers.push(setTimeout(() => setScanning(false), PROCESSING_STEPS.length * PROCESSING_STEP_MS));
    return () => timers.forEach(clearTimeout);
  }, [open, scanKey]);
  const stepLabel = PROCESSING_STEPS[stepIndex];

  // Rerun = recompute the recommendation upstream, then replay the scan.
  const handleRerun = () => {
    onRerun();
    setScanKey((k) => k + 1);
  };

  const toggleProposal = (id: string) =>
    setConfirmedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const showExceptions = tab === 'All' || tab === 'Urgent';
  const showSwaps = tab === 'All' || tab === 'Swaps';
  const showProposals = tab === 'All' || tab === 'Backlog';
  const showCapacity = true; // capacity forecast shows on every filter

  // Per-tab counts (mirrors the Manifest panel's chip counts).
  const tabCount: Record<Tab, number> = {
    All: exceptions.length + swaps.length + proposals.length,
    Urgent: exceptions.length,
    Backlog: proposals.length,
    Swaps: swaps.length,
  };

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        'absolute inset-y-0 right-0 z-40 flex w-[26rem] max-w-[92%] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out',
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
      )}
    >
      {/* "my i" bar — the icon closes the panel (matches Plan / Manifest). */}
      <div className="flex w-full items-center gap-2.5 border-b border-slate-700 bg-slate-800 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close booking intelligence"
          title="Close"
          className="shrink-0 rounded-full p-0.5 transition-colors hover:bg-slate-700"
        >
          <MyIIcon state={scanning ? 'scanning' : 'idle'} className="h-7 w-7" />
        </button>
        <div className="leading-tight">
          <div className="text-xs font-medium text-slate-100">my i</div>
          <div className="text-[10px] text-slate-400">
            {scanning ? `${stepLabel}…` : 'Booking Intelligence'}
          </div>
        </div>
        <span className="ml-auto whitespace-nowrap text-[9px] text-slate-200">
          {depotLabel} · {horizonLabel} · {pipelineCount} to book
        </span>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-slate-200 px-3 py-2">
        {TABS.map((t) => {
          const active = t === tab;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors',
                active
                  ? 'border-slate-700 bg-slate-700 text-white'
                  : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
              )}
            >
              {t}
              {!scanning && tabCount[t] > 0 && (
                <span className="tabular-nums opacity-60">{tabCount[t]}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {scanning ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
            <MyIIcon state="scanning" className="h-16 w-16" />
            <p key={stepLabel} className="animate-pulse text-sm font-medium text-slate-600">
              {stepLabel}…
            </p>
            <p className="text-xs text-slate-400">
              Scanning the pipeline — checking sailings, cutoffs and block capacity.
            </p>
          </div>
        ) : (
          <div className="space-y-3 p-3 text-xs">
            {/* Rerun — recompute the suggestions against the live clock. */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
              <p className="text-[11px] text-slate-600">
                Rerun the business intelligence to get fresh suggested routes with cutoff times.
              </p>
              <button
                type="button"
                onClick={handleRerun}
                disabled={rerunning || scanning}
                title="Rerun the route recommendation and refresh cutoffs"
                className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:border-slate-500 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw
                  className={cn('h-3.5 w-3.5', (rerunning || scanning) && 'animate-spin')}
                  aria-hidden="true"
                />
                Rerun suggestions
              </button>
            </div>

            {/* Exceptions */}
            {showExceptions &&
          exceptions.map((e) => (
            <div key={e.id} className="rounded-md border border-red-200 bg-red-50 p-2.5">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {e.orderNumber} — {e.customer}
              </div>
              <div className="mt-1 text-[10px] text-red-600/80">
                {e.corridor} · {e.cargoLabel} · {e.window} window
              </div>
              <p className="mt-1 text-[11px] text-slate-600">{e.reason}</p>
              <button
                type="button"
                className="mt-2 rounded-md border border-red-300 bg-white px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50"
              >
                Open booking screen →
              </button>
            </div>
          ))}

        {/* Capacity swaps — bump a slack booking to free a full block for an urgent one. */}
        {showSwaps && swaps.length > 0 && (
          <Block title="Capacity swaps">
            <div className="space-y-2">
              {swaps.map((s) => (
                <SwapCard
                  key={s.id}
                  s={s}
                  applied={appliedSwaps.has(s.id)}
                  onApply={() => setAppliedSwaps((prev) => new Set(prev).add(s.id))}
                  onDismiss={() => setSwaps((prev) => prev.filter((x) => x.id !== s.id))}
                />
              ))}
            </div>
          </Block>
        )}

        {/* Proposals */}
        {showProposals && proposals.length > 0 && (
          <Block title="Proposed — confirm to include">
            <div className="space-y-2">
              {proposals.map((p) => (
                <ProposalCard
                  key={p.id}
                  p={p}
                  confirmed={confirmedIds.has(p.id)}
                  onToggle={() => toggleProposal(p.id)}
                />
              ))}
            </div>
          </Block>
        )}

            {/* Capacity forecast */}
            {showCapacity && (
              <Block title="Capacity forecast — today">
                <div className="space-y-2">
                  {CAPACITY_FORECAST.map((c) => (
                    <CapacityCard key={`${c.operator}-${c.routeShort}`} c={c} />
                  ))}
                </div>
              </Block>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function ProposalCard({
  p,
  confirmed,
  onToggle,
}: {
  p: BookingProposal;
  confirmed: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={cn(
        'rounded-md border p-2.5 transition-colors',
        confirmed ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200 bg-white',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold text-slate-800">
            {p.orderNumber} — {p.customer}
          </div>
          <div className="text-[10px] text-slate-500">
            {p.routeShort}
            {p.sailTime ? ` ${p.sailTime}` : ''} · {p.operator} · {p.roadKm}km to {p.destination}
          </div>
        </div>
        <button type="button" onClick={onToggle} aria-pressed={confirmed} title="Toggle inclusion">
          <SailingTypePill type={p.type} />
        </button>
      </div>
      <p className="mt-1.5 text-[10px] italic text-slate-500">{p.note}</p>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'mt-2 w-full rounded-md px-2 py-1 text-[10px] font-medium transition-colors',
          confirmed
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
        )}
      >
        {confirmed ? '✓ Included' : 'Include'}
      </button>
    </div>
  );
}

/**
 * Capacity swap card — the urgent order that gets the freed slot over a down
 * arrow to the slack booking that moves to a later sailing. Apply queues the
 * reshuffle (mock); Dismiss drops the suggestion.
 */
function SwapCard({
  s,
  applied,
  onApply,
  onDismiss,
}: {
  s: SwapSuggestion;
  applied: boolean;
  onApply: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className={cn(
        'rounded-md border p-2.5',
        applied ? 'border-emerald-300 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/50',
      )}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-800">
        <Repeat2 className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
        Capacity swap · {s.route}
        <span className="ml-auto rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">
          {s.blockNote}
        </span>
      </div>

      {/* Urgent — takes the freed slot */}
      <div className="mt-2 rounded-md border border-red-200 bg-white px-2 py-1.5">
        <div className="text-[11px] font-semibold text-slate-800">
          {s.urgent.orderNumber} — {s.urgent.customer}
        </div>
        <div className="text-[10px] text-red-600">{s.urgent.need}</div>
      </div>

      <div className="flex items-center justify-center py-0.5 text-slate-400">
        <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
      </div>

      {/* Bump — moves to a later sailing */}
      <div className="rounded-md border border-slate-200 bg-white px-2 py-1.5">
        <div className="text-[11px] font-semibold text-slate-800">
          {s.bump.orderNumber} — {s.bump.customer}
        </div>
        <div className="text-[10px] text-slate-500">
          {s.bump.slack} · move to {s.bump.moveTo}
        </div>
      </div>

      <p className="mt-1.5 text-[10px] italic text-slate-500">{s.rationale}</p>

      {applied ? (
        <div className="mt-2 rounded-md bg-emerald-600 px-2 py-1 text-center text-[10px] font-medium text-white">
          ✓ Swap queued — runs with the next booking run
        </div>
      ) : (
        <div className="mt-2 flex gap-1.5">
          <button
            type="button"
            onClick={onApply}
            className="flex-1 rounded-md bg-amber-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-amber-700"
          >
            Apply swap
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-500 hover:bg-slate-50"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Per-route capacity strip. A thin three-segment bar (off-peak block / off-peak
 * open / peak) with a needle showing units needed, plus slot chips. Colour
 * escalates: block covers (emerald), into open market (amber), peak required (red).
 */
function CapacityCard({ c }: { c: CapacityForecastRow }) {
  const { tone, label } = capacityTier(c);
  const totalSlots = c.offPeakBlock + c.offPeakOpen + c.peakBlock + c.peakOpen;
  const pct = (n: number) => (totalSlots > 0 ? `${(n / totalSlots) * 100}%` : '0%');

  return (
    <div className="rounded-md border border-slate-200 p-2">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-medium text-slate-700">
          {c.operator} · {c.routeShort}
        </div>
        <span className={cn('text-[10px] font-semibold', tone.text)}>{c.needed} needed</span>
      </div>

      {/* Stacked capacity track */}
      <div className="mt-1.5 flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="bg-emerald-300" style={{ width: pct(c.offPeakBlock) }} title="Off-peak block" />
        <div className="bg-emerald-100" style={{ width: pct(c.offPeakOpen) }} title="Off-peak open" />
        <div className="bg-amber-200" style={{ width: pct(c.peakBlock + c.peakOpen) }} title="Peak" />
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] text-slate-400">
        <span>Off-peak blk {c.offPeakBlock}</span>
        <span>· open {c.offPeakOpen}</span>
        <span>· peak {c.peakBlock + c.peakOpen}</span>
        <span className={cn('ml-auto rounded-full px-1.5 py-0.5 font-medium', tone.chip)}>{label}</span>
      </div>
    </div>
  );
}

/** Capacity tier — how the demand sits against the off-peak block / pools. */
function capacityTier(c: CapacityForecastRow): {
  label: string;
  tone: { text: string; chip: string };
} {
  const offPeakTotal = c.offPeakBlock + c.offPeakOpen;
  if (c.needed > offPeakTotal) {
    return { label: 'Peak required', tone: { text: 'text-red-600', chip: 'bg-red-50 text-red-600' } };
  }
  if (c.needed > c.offPeakBlock) {
    return {
      label: 'Open market',
      tone: { text: 'text-amber-600', chip: 'bg-amber-50 text-amber-600' },
    };
  }
  return {
    label: 'Block covers',
    tone: { text: 'text-emerald-600', chip: 'bg-emerald-50 text-emerald-600' },
  };
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {title}
      </div>
      {children}
    </div>
  );
}
